import React from "react";
import { useQuery } from "@tanstack/react-query";
import { formatInTimeZone } from "date-fns-tz";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import Typography from "@/components/ui/typography";
import { CohortsKeys, getAllCohorts } from "@/features/cohorts/api/cohorts";

interface CohortDetailsDialogProps {
  cohortId: string | null;
  onClose: () => void;
}

const CohortDetailsDialog: React.FC<CohortDetailsDialogProps> = ({
  cohortId,
  onClose,
}) => {
  const { data: cohorts } = useQuery({
    queryKey: [CohortsKeys.GET_COHORTS],
    queryFn: getAllCohorts,
    enabled: !!cohortId,
  });

  const cohort = cohorts?.find((c) => c._id === cohortId);

  const renderValue = (value: any, type: "string" | "date" = "string") => {
    if (type === "date") {
      if (!value)
        return (
          <Typography size="l2" className="text-muted-foreground">
            Not set
          </Typography>
        );
      return (
        <Typography size="l2">
          {formatInTimeZone(value, "America/New_York", "MMMM d, yyyy 'at' h:mm a")}
        </Typography>
      );
    }
    return (
      <Typography
        size="l2"
        className={!value ? "text-muted-foreground" : undefined}
      >
        {value || "Not set"}
      </Typography>
    );
  };

  return (
    <Dialog open={!!cohortId} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Cohort Details</DialogTitle>
        </DialogHeader>

        {!cohort ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <ScrollArea className="h-[50vh] pr-2">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Typography
                    size="l2"
                    className="font-medium text-muted-foreground"
                  >
                    Cohort Number
                  </Typography>
                  <div className="mt-1">{renderValue(`#${cohort.number}`)}</div>
                </div>
                <div>
                  <Typography
                    size="l2"
                    className="font-medium text-muted-foreground"
                  >
                    Start Date
                  </Typography>
                  <div className="mt-1">
                    {renderValue(cohort.startDate, "date")}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Typography
                    size="l2"
                    className="font-medium text-muted-foreground"
                  >
                    Number of Founders
                  </Typography>
                  <div className="mt-1 font-semibold">
                    {renderValue(cohort.foundersCount)}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Typography
                    size="l2"
                    className="font-medium text-muted-foreground"
                  >
                    Created At
                  </Typography>
                  <div className="mt-1">
                    {renderValue(cohort.createdAt, "date")}
                  </div>
                </div>
                <div>
                  <Typography
                    size="l2"
                    className="font-medium text-muted-foreground"
                  >
                    Updated At
                  </Typography>
                  <div className="mt-1">
                    {renderValue(cohort.updatedAt, "date")}
                  </div>
                </div>
              </div>
              <div>
                <Typography
                  size="l2"
                  className="font-medium text-muted-foreground"
                >
                  Internal ID
                </Typography>
                <p className="mt-1 font-mono text-sm break-all">
                  {renderValue(cohort._id)}
                </p>
              </div>
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CohortDetailsDialog;
